import React, { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { SearchOutlined, TeamOutlined, TrophyOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import EmptyState from '../components/EmptyState';
import GamePlayerPortrait from '../components/GamePlayerPortrait';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  AUTOSUGGEST_MIN_CHARACTERS,
  createDraftOrder,
  createFranchiseReel,
  createFranchiseSequence,
  DRAFT_ORDER,
  remainingSlots,
  ROSTER_LIMITS,
  rosterSize,
  searchEligiblePlayers,
  teamScore,
} from '../games/gameLogic';
import { useGamesData } from '../hooks/useGamesData';
import { DraftTeam, DraftedPlayer, GameFranchise, GamePlayer, GamePosition, RosterLimits } from '../types/games';
import './Games.css';

type DraftPhase = 'setup' | 'drafting' | 'results';
const POSITION_ORDER: GamePosition[] = ['QB', 'RB', 'WR', 'TE'];
const MAX_POSITION_SLOTS = 4;

const DraftRoster: React.FC<{
  team: DraftTeam;
  active?: boolean;
  lastPickId?: number;
  rosterLimits: RosterLimits;
}> = ({ team, active = false, lastPickId, rosterLimits }) => {
  const slots = remainingSlots(team, rosterLimits);
  return (
    <section className={`draft-roster ${active ? 'is-active' : ''}`}>
      <header><div><span>{active ? 'ON THE CLOCK' : 'LINEUP'}</span><h2>{team.Name}</h2></div><strong>{teamScore(team).toFixed(1)}<small> PPG</small></strong></header>
      <div className="draft-roster-list">
        {POSITION_ORDER.flatMap((position) => {
          const players = team.Players.filter((player) => player.Position === position);
          return Array.from({ length: rosterLimits[position] }, (_, index) => {
            const player = players[index];
            return (
              <div className={`draft-roster-slot position-${position.toLowerCase()} ${player ? 'is-filled' : ''} ${player?.PlayerId === lastPickId ? 'is-last-pick' : ''}`} key={`${position}-${index}`}>
                <span>{position}{rosterLimits[position] > 1 ? index + 1 : ''}</span>
                {player ? (
                  <>
                    <GamePlayerPortrait name={player.Name} position={player.Position} url={player.HeadshotUrl} />
                    <div><strong>{player.Name}</strong><small>{player.FranchiseName} · {player.GamesStarted} starts</small></div>
                    <b>{player.PointsPerGame.toFixed(1)} <small>PPG</small></b>
                  </>
                ) : <em>{slots[position]} {position} slot{slots[position] === 1 ? '' : 's'} open</em>}
              </div>
            );
          });
        })}
      </div>
    </section>
  );
};

const FranchiseDraftGame: React.FC = () => {
  const { data, isLoading, error } = useGamesData();
  const [phase, setPhase] = useState<DraftPhase>('setup');
  const [teamNames, setTeamNames] = useState(['Team 1', 'Team 2']);
  const [teams, setTeams] = useState<DraftTeam[]>([]);
  const [rosterLimits, setRosterLimits] = useState<RosterLimits>({ ...ROSTER_LIMITS });
  const [draftOrder, setDraftOrder] = useState<number[]>(DRAFT_ORDER);
  const [franchiseSequence, setFranchiseSequence] = useState<ReturnType<typeof createFranchiseSequence>>([]);
  const [pickIndex, setPickIndex] = useState(0);
  const [search, setSearch] = useState('');
  const [lastPick, setLastPick] = useState<DraftedPlayer | null>(null);
  const [franchiseReel, setFranchiseReel] = useState<GameFranchise[]>([]);
  const [reelIndex, setReelIndex] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const draftedPlayerIdsRef = useRef(new Set<number>());
  const pickCommittedRef = useRef(false);

  const teamIndex = draftOrder[pickIndex] ?? 0;
  const currentTeam = teams[teamIndex];
  const currentFranchise = franchiseSequence[pickIndex];
  const displayedFranchise = isSpinning ? franchiseReel[reelIndex] || currentFranchise : currentFranchise;
  const draftedIds = useMemo(() => new Set(teams.flatMap((team) => team.Players.map((player) => player.PlayerId))), [teams]);
  const suggestions = useMemo(() => {
    if (isSpinning || !currentFranchise || !currentTeam) return [];
    return searchEligiblePlayers(currentFranchise, currentTeam, draftedIds, search, rosterLimits);
  }, [currentFranchise, currentTeam, draftedIds, isSpinning, rosterLimits, search]);

  const configuredRosterSize = rosterSize(rosterLimits);

  useEffect(() => {
    if (!isSpinning || franchiseReel.length === 0) return undefined;

    let nextIndex = 0;
    const timer = window.setInterval(() => {
      nextIndex += 1;
      if (nextIndex >= franchiseReel.length) {
        window.clearInterval(timer);
        setIsSpinning(false);
        setFranchiseReel([]);
        setReelIndex(0);
        pickCommittedRef.current = false;
        return;
      }
      setReelIndex(nextIndex);
    }, 90);

    return () => window.clearInterval(timer);
  }, [franchiseReel, isSpinning]);

  const beginDraft = () => {
    if (!data?.Franchises.length || configuredRosterSize === 0) return;
    const names = teamNames.map((name, index) => name.trim() || `Team ${index + 1}`);
    const nextDraftOrder = createDraftOrder(configuredRosterSize);
    setTeamNames(names);
    setTeams(names.map((Name) => ({ Name, Players: [] })));
    setDraftOrder(nextDraftOrder);
    setFranchiseSequence(createFranchiseSequence(data.Franchises, nextDraftOrder.length));
    setPickIndex(0);
    setSearch('');
    setLastPick(null);
    setFranchiseReel([]);
    setReelIndex(0);
    setIsSpinning(false);
    draftedPlayerIdsRef.current = new Set();
    pickCommittedRef.current = false;
    setPhase('drafting');
  };

  const draftPlayer = (player: GamePlayer) => {
    if (!data || !currentFranchise || !currentTeam) return;
    if (pickCommittedRef.current) return;
    if (draftedPlayerIdsRef.current.has(player.PlayerId)) return;
    pickCommittedRef.current = true;
    draftedPlayerIdsRef.current.add(player.PlayerId);
    const drafted: DraftedPlayer = {
      ...player,
      FranchiseId: currentFranchise.Id,
      FranchiseName: currentFranchise.Name,
      FranchiseLogoUrl: currentFranchise.LogoUrl,
    };
    setTeams((current) => current.map((team, index) => (
      index === teamIndex ? { ...team, Players: [...team.Players, drafted] } : team
    )));
    setLastPick(drafted);
    setSearch('');
    if (pickIndex === draftOrder.length - 1) {
      setPickIndex((current) => current + 1);
      setPhase('results');
    } else {
      const nextPickIndex = pickIndex + 1;
      setPickIndex(nextPickIndex);
      setFranchiseReel(createFranchiseReel(data.Franchises, franchiseSequence[nextPickIndex]));
      setReelIndex(0);
      setIsSpinning(true);
    }
  };

  const restart = () => {
    setTeams([]);
    setFranchiseSequence([]);
    setPickIndex(0);
    setSearch('');
    setLastPick(null);
    setFranchiseReel([]);
    setReelIndex(0);
    setIsSpinning(false);
    draftedPlayerIdsRef.current = new Set();
    pickCommittedRef.current = false;
    setPhase('setup');
  };

  const adjustRosterLimit = (position: GamePosition, change: number) => {
    setRosterLimits((current) => ({
      ...current,
      [position]: Math.min(MAX_POSITION_SLOTS, Math.max(0, current[position] + change)),
    }));
  };

  if (isLoading) return <div className="games-page"><LoadingSpinner message="Loading franchise alumni…" /></div>;
  if (error || !data) return <div className="games-page"><EmptyState message="Game data is unavailable" description="Run the game-data export and refresh this page." /></div>;

  if (phase === 'setup') {
    return (
      <div className="games-page franchise-draft-page">
        <Link to="/games" className="games-back">← League Games</Link>
        <header className="games-hero games-hero--compact">
          <div className="games-eyebrow"><TeamOutlined /> Two-player pass & play</div>
          <h1>Franchise Draft</h1>
          <p>Each turn gives you a random league franchise. Draft an alumnus who fills an open lineup slot.</p>
        </header>
        <section className="game-setup-card draft-setup-card">
          <div className="draft-rules">
            <span><strong>{configuredRosterSize}</strong> picks each</span>
            <span><strong>{POSITION_ORDER.map((position) => rosterLimits[position]).join('–')}</strong> QB · RB · WR · TE</span>
            <span><strong>PPG</strong> decides it</span>
          </div>
          <div className="draft-config-header">
            <div><strong>Roster configuration</strong><small>Choose 0–{MAX_POSITION_SLOTS} slots at each position.</small></div>
            <button type="button" onClick={() => setRosterLimits({ ...ROSTER_LIMITS })}>Reset default</button>
          </div>
          <div className="draft-roster-config" aria-label="Roster configuration">
            {POSITION_ORDER.map((position) => (
              <div className={`draft-position-control position-${position.toLowerCase()}`} key={position}>
                <span>{position}</span>
                <button type="button" onClick={() => adjustRosterLimit(position, -1)} disabled={rosterLimits[position] === 0} aria-label={`Remove one ${position} slot`}>−</button>
                <strong>{rosterLimits[position]}</strong>
                <button type="button" onClick={() => adjustRosterLimit(position, 1)} disabled={rosterLimits[position] === MAX_POSITION_SLOTS} aria-label={`Add one ${position} slot`}>+</button>
              </div>
            ))}
          </div>
          <div className="draft-name-fields">
            {teamNames.map((name, index) => (
              <label key={index}>Team {index + 1} name<input value={name} maxLength={24} onChange={(event) => setTeamNames((current) => current.map((item, itemIndex) => itemIndex === index ? event.target.value : item))} /></label>
            ))}
          </div>
          <p className="draft-order-note">Team 1 picks once. Team 2 gets two picks, then turns alternate in pairs until both lineups are full.</p>
          {configuredRosterSize === 0 && <p className="draft-config-error">Add at least one roster slot to start.</p>}
          <button type="button" className="game-primary-button" disabled={configuredRosterSize === 0} onClick={beginDraft}>Draw the first franchise</button>
        </section>
      </div>
    );
  }

  if (phase === 'results') {
    const scores = teams.map(teamScore);
    const winnerIndex = scores[0] === scores[1] ? -1 : scores[0] > scores[1] ? 0 : 1;
    return (
      <div className="games-page franchise-draft-page draft-results-page">
        <Link to="/games" className="games-back">← League Games</Link>
        <header className="draft-winner">
          <TrophyOutlined />
          <span>{winnerIndex === -1 ? 'DEAD EVEN' : 'FRANCHISE DRAFT CHAMPION'}</span>
          <h1>{winnerIndex === -1 ? `${teams[0].Name} & ${teams[1].Name}` : teams[winnerIndex].Name}</h1>
          <p>{scores[0].toFixed(1)}–{scores[1].toFixed(1)} combined franchise PPG</p>
        </header>
        <div className="draft-results-rosters">
          <DraftRoster team={teams[0]} rosterLimits={rosterLimits} />
          <DraftRoster team={teams[1]} rosterLimits={rosterLimits} />
        </div>
        <div className="game-result-actions"><button type="button" className="game-primary-button" onClick={beginDraft}>Run it back</button><button type="button" className="game-secondary-button" onClick={restart}>Change team names</button></div>
        <p className="game-scoring-note">{data.ScoringNote}</p>
      </div>
    );
  }

  const style = { '--franchise-color': displayedFranchise.Color || '#1a2b4c' } as CSSProperties;
  const openSlots = remainingSlots(currentTeam, rosterLimits);
  return (
    <div className="games-page franchise-draft-page draft-board-page" style={style}>
      <div className="draft-topbar"><Link to="/games" className="games-back">← Exit game</Link><span>Pick {pickIndex + 1} / {draftOrder.length}</span></div>
      <div className="draft-board-grid">
        <DraftRoster team={teams[0]} active={teamIndex === 0} lastPickId={lastPick?.PlayerId} rosterLimits={rosterLimits} />
        <main className="draft-picker">
          <header className={`draft-franchise-prompt ${isSpinning ? 'is-spinning' : ''}`}>
            <span>{isSpinning ? `${currentTeam.Name} draws next` : `${currentTeam.Name} is on the clock`}</span>
            <div className="draft-franchise-reel-window" aria-hidden={isSpinning}>
              <div className="draft-franchise-reel-item" key={`${displayedFranchise.Id}-${reelIndex}`}>
                <img src={displayedFranchise.LogoUrl} alt="" />
                <h1>{displayedFranchise.Name}</h1>
              </div>
            </div>
            <p role={isSpinning ? 'status' : undefined}>{isSpinning ? 'Randomizing franchise…' : 'Draft a player who started for this franchise.'}</p>
            {!isSpinning && <div className="draft-open-positions">{POSITION_ORDER.map((position) => <span className={openSlots[position] ? '' : 'is-full'} key={position}>{position} {openSlots[position]}</span>)}</div>}
          </header>
          {isSpinning ? <div className="draft-reel-spacer" /> : (
            <>
              <label className="draft-player-search">
                <SearchOutlined />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Type at least 3 letters"
                  role="combobox"
                  aria-autocomplete="list"
                  aria-controls="draft-player-options"
                  aria-expanded={search.trim().length >= AUTOSUGGEST_MIN_CHARACTERS && suggestions.length > 0}
                  autoComplete="off"
                  autoFocus
                />
              </label>
              <div className="draft-suggestions" id="draft-player-options" role="listbox">
                {search.trim().length >= AUTOSUGGEST_MIN_CHARACTERS && (
                  suggestions.length ? suggestions.map((player) => (
                    <button type="button" role="option" aria-selected="false" className={`draft-suggestion position-${player.Position.toLowerCase()}`} onClick={() => draftPlayer(player)} key={player.PlayerId}>
                      <GamePlayerPortrait name={player.Name} position={player.Position} url={player.HeadshotUrl} />
                      <span><small>{player.Position}</small><strong>{player.Name}</strong></span>
                    </button>
                  )) : <p className="draft-no-match">No eligible player matches that name.</p>
                )}
              </div>
            </>
          )}
        </main>
        <DraftRoster team={teams[1]} active={teamIndex === 1} lastPickId={lastPick?.PlayerId} rosterLimits={rosterLimits} />
      </div>
    </div>
  );
};

export default FranchiseDraftGame;
